import { http, queryString } from 'utils'
import { omit, concat, map, set, findIndex } from 'lodash'
import { UserDecorator } from 'decorators'

// Driver mutation imports.
import {
  SET_DRIVERS, SET_DRIVERS_PAGINATION, ADD_DRIVERS, SET_DRIVER, RESET_DRIVER, SET_DRIVER_ATTRIBUTE, PUSH_DRIVER, ADD_DRIVER
} from 'vx/mutations'

const initialDriverState = () => {
  return {
    id: null,
    licence: null,
    licence_expiry: null,
    photo_storage_path: null,
    photo_name: null
  }
}

// Driver state.
const state = {
  drivers: [],
  driver: initialDriverState()
}

// Driver state getters.
const getters = {
  /**
   * Selectable drivers for selectize.
   */
  driverOptions: state => map(state.drivers, driver => ({ id: driver.id, text: driver.profile ? `${driver.profile.name} ${driver.profile.surname}` : null })),

  /**
   * Driver options with 'None' for drop down..
   */
  driverOptionsWithNone: (state, getters) => concat([{ id: '', text: '<em>None</em>' }], getters.driverOptions),

  /**
   * Drivers decorated via the DriverDecorator.
   */
  driversDecorated: state => map(state.drivers, driver => (new UserDecorator(driver)))
}

// Driver actions.
const actions = {
  /**
   * Get a list of drivers based on user.
   */
  getDrivers ({ commit }, payload) {
    return http.get(`drivers?${queryString(payload)}`).then(response => {
      commit(SET_DRIVERS, response.data.data)
      commit(SET_DRIVERS_PAGINATION, omit(response.data, 'data'))

      return response
    })
  },

  /**
   * Get the drivers for the next page.
   */
  getDriversForNextPage ({ commit, state }, payload) {
    if (state.pagination.current_page === state.pagination.last_page || state.pagination.total === 0) {
      return Promise.reject('There are no more drivers to load.')
    }

    let page = state.pagination.current_page + 1

    return http.get(`drivers?${queryString(payload)}&page=${page}`).then(response => {
      commit(ADD_DRIVERS, response.data.data)
      commit(SET_DRIVERS_PAGINATION, omit(response.data, 'data'))

      return response
    })
  },

  /**
   * Store a new driver.
   */
  storeDriver ({ commit }, payload) {
    return http.post(`drivers`, payload).then(response => {
      commit(PUSH_DRIVER, response.data)

      return response
    })
  },

  /**
   * Update an existing driver's data.
   */
  updateDriver ({ commit }, { id, payload }) {
    return http.put(`drivers/${id}`, payload).then(response => {
      commit(SET_DRIVER, response.data)

      return response
    })
  },

  /**
   * Retrieve a driver by ID.
   */
  getDriver ({ commit, state }, id) {
    return http.get(`users/${id}`).then(response => {
      commit(SET_DRIVER, response.data)

      return state.driver
    })
  },

  /**
   * Delete a driver.
   */
  deleteDriver ({ commit }, id) {
    return http.delete(`drivers/${id}`).then(response => {
      commit(SET_DRIVER, initialDriverState())

      return response
    })
  },

  /**
   * Upload a driver photo and crop dimensions.
   */
  uploadDriverPhoto (store, { id, file, crop }) {
    let payload = new FormData()

    payload.append('photo', file)
    payload.append('crop[x]', crop.x)
    payload.append('crop[y]', crop.y)
    payload.append('crop[width]', crop.width)
    payload.append('crop[height]', crop.height)

    return http.post(`drivers/${id}/photos`, payload)
  },

  /**
   * Get a driver photos data URI for a given dimension.
   */
  getDriverPhotoDataUri (store, { id, dimension }) {
    return http.get(`drivers/${id}/photos/${dimension}`)
  }
}

// Driver state mutations.
const mutations = {
  /**
   * Set the drivers.
   */
  [SET_DRIVERS] (state, drivers) {
    state.drivers = drivers
  },

  /**
   * Add more drivers to the drivers state.
   */
  [ADD_DRIVERS] (state, drivers) {
    state.drivers = concat(state.drivers, drivers)
  },

  /**
   * Set the driver pagination.
   */
  [SET_DRIVERS_PAGINATION] (state, pagination) {
    state.pagination = pagination
  },

  /**
   * Set the state driver.
   */
  [SET_DRIVER] (state, driver) {
    state.driver = driver
  },

  /**
   * Add a new driver or replace the existing one.
   */
  [ADD_DRIVER] (state, driver) {
    let existingDriver = findIndex(state.drivers, existing => existing.id === driver.id)

    if (existingDriver === -1) {
      state.drivers.push(driver)
    } else {
      state.drivers.splice(existingDriver, 1, driver)
    }
  },

  /**
   * Reset the driver.
   */
  [RESET_DRIVER] (state) {
    state.driver = initialDriverState()
  },

  /**
   * Set drivers attribute.
   */
  [SET_DRIVER_ATTRIBUTE] (state, { attribute, value }) {
    set(state.driver, attribute, value)
  },

  /**
   * Push a driver onto the drivers array.
   */
  [PUSH_DRIVER] (state, driver) {
    state.drivers.push(driver)
  }
}

// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, state, getters, actions, mutations
}



// WEBPACK FOOTER //
// ./src/vuex/modules/driver.js